import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #submenuMutationObserver = null;
  #state = { activeItem: null, activeOverflowItem: null };

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('click', this.#clickListener);
    document.addEventListener('pointerdown', this.#outsidePointerDownListener);
    document.addEventListener('keydown', this.#keyDownListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.#clickListener);
    document.removeEventListener('pointerdown', this.#outsidePointerDownListener);
    document.removeEventListener('keydown', this.#keyDownListener);
    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => setHeaderMenuStyle(), 100);

  #clickListener = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (!item) return;
    const listItem = item.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;
    const submenu = findSubmenu(item);
    const isMoreTrigger = listItem.slot === 'more';
    if (!submenu && !isMoreTrigger) return;

    event.preventDefault();
    if (item === this.#state.activeItem && item.ariaExpanded === 'true') {
      this.#deactivate(item);
      return;
    }
    this.activate(event);
  };

  #outsidePointerDownListener = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;
    this.#deactivate();
  };

  #keyDownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const item = this.#state.activeItem;
    this.#deactivate(item);
    item.focus();
  };

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') ?? null;
  }

  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  activate = (event) => {
    if (event instanceof PointerEvent && event.type === 'pointerenter') return;
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previousItem = this.#state.activeItem;
    const previousOverflowItem = this.#state.activeOverflowItem;
    if (previousItem) {
      previousItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previousItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }
    if (previousOverflowItem && previousOverflowItem !== previousItem) {
      previousOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) overflowItem.ariaExpanded = 'true';

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);
    if (!hasSubmenu && !isDefaultSlot) submenu = this.overflowMenu;

    if (submenu instanceof HTMLElement) {
      submenu.dataset.active = '';
      this.dispatchEvent(new MegaMenuHoverEvent());
      this.#observeSubmenuHeight(submenu);
    }

    let finalHeight = submenu instanceof HTMLElement ? submenu.offsetHeight : 0;
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      const overflowHeight = this.overflowMenu?.offsetHeight || 0;
      finalHeight = hasSubmenu ? Math.max(overflowHeight, overflowListHeight) : overflowListHeight;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (event instanceof PointerEvent && event.type === 'pointerleave') return;
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;
    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;
    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) activeOverflowItem.ariaExpanded = 'false';
    if (submenu instanceof HTMLElement) delete submenu.dataset.active;
    this.#cleanupMutationObserver();
  };

  #observeSubmenuHeight(submenu) {
    this.#cleanupMutationObserver();
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (submenu.offsetHeight > 0 && this.#state.activeItem) {
          const height = submenu.offsetHeight;
          const headerVisibleHeight = this.#getHeaderVisibleHeight();
          this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
          this.#setFullOpenHeaderHeight(height, headerVisibleHeight);
          this.#cleanupMutationObserver();
        }
      }));
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => this.#cleanupMutationObserver(), 500);
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;
    const submenus = slottedMenuLinks.map((link) => link.querySelector('[ref="submenu[]"]')).filter((node) => node instanceof HTMLElement);
    submenus.forEach((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    submenus.forEach((submenu) => submenu.style.removeProperty('display'));
    return height;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;
    const isOverlap = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    if (isOverlap && this.headerComponent.offsetHeight > 0) {
      return this.headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0;
    }
    return this.headerComponent.offsetHeight;
  }

  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;
    const fullHeight = submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight;
    this.headerComponent.style.setProperty('--full-open-header-height', `${fullHeight}px`);
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  const closest = element.closest('[ref="menuitem"]');
  return closest instanceof HTMLElement ? closest : null;
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
